import { PhysicalWindowState, Rectangle } from "../../../FEA";

import { WINDOWSTATE } from "../../../common/constants";

type FinsembleConfig = Record<string, any>;

export type Bounds = {
	top: number;
	left: number;
	width: number;
	height: number;
	bottom?: number;
	right?: number;
};

export type PercentBounds = {
	percentTop: string;
	percentLeft: string;
	percentWidth: string;
	percentHeight: string;
};

/**
 * Contains all the optional keys on the FinsembleWindowData
 * type. See FinsembleWindowData for more info.
 *
 */
export type OptionalFinsembleWindowData = {
	/** DH 5/8/2019
	 * Confusingly, this is not the "official" component state.
	 * That's stored under an entirely different key. I'm not
	 * sure why or how this is here, but I definitely saw it
	 * at runtime.
	 */
	componentState: Record<string, any>;
	alphaMask: {
		blue: number;
		green: number;
		red: number;
	};
	allowAutoArrange: boolean;
	alwaysOnBottom: boolean;
	alwaysOnTop: boolean;
	applicationIcon: string;
	autoShow: boolean;
	backgroundColor: string;
	backgroundThrottling: boolean;
	bottom: number | string;
	bounds: Bounds;
	callstack: string;
	center: boolean;
	childWindowIdentifiers: [any];
	contextMenu: boolean;
	cornerRounding: {
		height: number;
		width: number;
	};
	customData: any;
	defaultCentered: boolean;
	defaultHeight: number;
	defaultLeft: number;
	defaultTop: number;
	defaultWidth: number;
	delay_connection: boolean;
	disableIabSecureLogging: boolean;
	draggable: boolean;
	"enable-plugins": boolean;
	enableLargerThanScreen: boolean;
	exitOnClose: boolean;
	experimental: {
		disableInitialReload: boolean;
		v2Api: boolean;
	};
	/** DH 6/14/2019
	 * We added this property because
	 * Electron/OF maintain their own
	 * record of which window has focus,
	 * creating the possibility for the
	 * OS and Electron/OF to think different
	 * windows have focus. Currently, we
	 * only keep this state on WebWindows.
	 * If we can figure out a different way
	 * to synchronize the OS and container,
	 * we may not need this state.
	 */
	focused: boolean;
	frame: boolean;
	frameConnect: string;
	hasLoaded: boolean;
	height: number | string;
	hideOnBlur: boolean;
	hideOnClose: boolean;
	hideOnce: boolean;
	hideWhileChildrenVisible: boolean;
	icon: string;
	isEvergreen: boolean;
	launchGroupComponentData: {
		launchGroupId?: string;
		launchGroupComponentUUID: string;
		closeComponentsTogether: boolean;
	};
	launchExternal: "";
	left: number | string;
	loadErrorMessage: "";
	maxHeight: number;
	maxWidth: number;
	maximizable: boolean;
	monitorInfo: number;
	minHeight: number;
	minWidth: number;
	minimizable: boolean;
	monitorDimensions: {
		top: number;
		bottom: number;
		left: number;
		right: number;
		height: number;
		width: number;
	};
	normalBounds?: Rectangle;
	opacity: number;
	options: any;
	percentTop: string;
	percentBottom: string;
	percentLeft: string;
	percentRight: string;
	percentWidth: string;
	percentHeight: string;
	plugin: Record<string, any>[];
	plugins: boolean;
	preloadScripts: {
		state?: "succeeded";
		url: string;
	}[];
	resizable: boolean;
	resize: boolean;
	resizeRegion: {
		bottomRightCorner: number;
		sides: {
			top: number;
			bottom: number;
			left: number;
			right: number;
		};
		size: number;
	};
	right: number | string;
	saveWindowState: boolean;
	shadow: boolean;
	show: boolean;
	showTaskbarIcon: boolean;
	skipTaskbar: boolean;
	smallWindow: boolean;
	state: string;
	spawnedByWorkspaceService: boolean;
	taskbarIcon: "";
	taskbarIconGroup: string;
	title: string;
	top: number | string;
	transparent: boolean;
	type: string;
	url: string;
	uuid: string;
	waitForPageLoad: boolean;
	webPreferences: {
		disableInitialReload: boolean;
		nodeIntegration: boolean;
		plugins: boolean;
	};
	webSecurity: boolean;
	width: number | string;
	windowIdentifier: {
		name: string;
		uuid: string;
		windowName: string;
		windowType: "WebWindow" | "NativeWindow" | "FinsembleNativeWindow";
	};
	windowState?: WINDOWSTATE;
	windowType: "WebWindow" | "NativeWindow" | "FinsembleNativeWindow";
	x: number;
	y: number;
};

// DH 3/18/2019 - This data has significant overlap with the `WindowDescriptor`
// and `SpawnParams` types. We need to pick one and stick with it.
/**
 * Represents all the state needed to recreate a Finsemble window.
 *
 * Pass to `Launcher.spawn` to spawn a new window with the given state.
 *
 * Once launched, the window saves its own state to storage, indexed by
 * the workspace it belongs to. This state is also partially managed by
 * the Workspace service.
 */
export type FinsembleWindowData = Partial<OptionalFinsembleWindowData> & {
	componentType: string;
	name: string;
};

export type IconUrl = {
	imageType: "url" | "fonticon";
	path: string;
};

export type IconInitials = {
	imageType: "initials";
	name: string;
};

export type IconDescriptor = (IconUrl | IconInitials) & {
	category?: string;
};

export type AppIcons = {
	taskbarIcon?: IconDescriptor;
	toolbarIcon: IconDescriptor;
	titleBarIcon: IconDescriptor;
};

export type WindowEventName =
	| "blurred"
	| "bounds-change-start"
	| "startedMoving"
	| "bounds-changing"
	| "bounds-change-request"
	| "bounds-change-end"
	| "bounds-changed"
	| "system-bounds-changed"
	| "system-maximized"
	| "system-restored"
	| "crashed"
	| "system-closed"
	// should deprecate in the future. this was used in floating titlebar.
	| "stoppedMoving"
	| "broughtToFront"
	| "clicked"
	| "close-requested"
	| "closed"
	// Used for public wrap cleanup only.
	| "alwaysOnTop"
	| "component-state-changed"
	| "close-complete"
	| "created"
	| "focused"
	| "hide-requested"
	| "hidden"
	| "maximize-requested"
	| "maximized"
	| "minimize-requested"
	| "minimized"
	| "parent-set"
	| "parent-unset"
	| "ready"
	| "restore-requested"
	| "restored"
	| "show-requested"
	| "shown"
	| "title-changed"
	| "wrap-state-changed";

export type WrapState = "created" | "initializing" | "ready" | "reloading" | "closing" | "closed";

export type WindowBounds = {
	top: number;
	left: number;
	width: number;
	height: number;
	right?: number;
	bottom?: number;
};

export type ShowWindowParams = any;

type LaunchGroupComponentType = {
	componentType: string;
};

type LaunchGroupComponentSpawnOptions = WindowBounds & {
	position: PositionTypes;
	addToWorkspace: boolean;
	data: Record<string, any>;
	monitor?: MonitorChoice;
};

export type LaunchGroupComponent = {
	componentType: LaunchGroupComponentType;
	spawnOptions: LaunchGroupComponentSpawnOptions;
	launchGroupComponentData?: LaunchGroupComponentData;
};

export type LaunchGroupStackedWindow = {
	components: LaunchGroupComponentType &
		{
			spawnOptions: {
				data: Record<string, any>;
			};
		}[];
	spawnOptions: LaunchGroupComponentSpawnOptions;
	launchGroupComponentData?: LaunchGroupComponentData;
};

export type LaunchGroupComponentData = {
	launchGroupId?: string;
	launchGroupComponentUUID: string;
	closeComponentsTogether: boolean;
};

/**
 * @deprecated - kept to ensure backwards compatibility, will be removed (or used by real composite components) in a future version
 */
export type CompositeComponentData = {
	compositeComponentUUID: string;
	closeComponentsTogether: boolean;
};

export type WindowLocator =
	| {
			/**
			 * The name of the physical HTML window, or a reference to a native window that was launched with Assimilation service
			 */
			windowName: string;
	  }
	| {
			//removed intersection type as docs generator can't handle an intersection under a union
			/**
			 * The type of component
			 */
			componentType: string;
			/**
			 * Optional uuid of a particular application process
			 */
			uuid?: string;
	  };

/**
 * An object that includes all the potential identifications for a window.
 * For instance, one can try and obtain a reference for a window if some of these values are known.
 */
export type WindowIdentifier = {
	/**
	 * The name of the physical HTML window, or a reference to a native window that was launched with Assimilation service
	 */
	windowName: string;
	/**
	 * The type of component
	 */
	componentType?: string | null;
	/**
	 * uuid is deprecated
	 */
	uuid?: string;
	windowType?: string;
	waitForReady?: boolean;
};

export type CustomData = {
	appConfig?: any;
	monitorDimensions?: any;
	window?: any;
	manifest?: any;
	spawnData?: any;
	foreign?: any;
	interop?: any;
	component?: any;
	isAnApp?: boolean;
};

// WindowInterop data is shared between the window service and the InteropService
export type WindowInterop = {
	connectId?: ConnectId; // connect id for auto-associate functionality
	launchGroupId?: string; // unique id set internally for launchGroup to support auto-associate functionality
	parent?: WindowIdentifier; // original app than opens another FDC3 app; set internally
	grandParent?: WindowIdentifier; // the app spawning a launch group; set internally for launchGroup
};

export type ConnectId = string | undefined | null;

/**
 * Used to define the parameters for creating a Window.
 */
export type WindowDescriptor = {
	name?: string;
	uuid?: string;
	monitorInfo?: any;
	defaultLeft?: number;
	defaultTop?: number;
	defaultBottom?: number;
	defaultWidth?: number;
	defaultHeight?: number;
	defaultRight?: number;
	/** The name of the component in this window. E.g "Welcome Component", etc. */
	componentType?: string;
	customData?: {
		appConfig?: any;
		monitorDimensions?: any;
		window?: any;
		manifest?: any;
		spawnData?: any;
		foreign?: any;
		interop?: any;
		component?: any;
		isAnApp?: boolean;
	};
	claimMonitorSpace?: boolean;
	canGroup?: boolean;
	url?: string;
	path?: string;
	manifest?: {};
	resizable?: boolean;
	/** Process affinity setting that allows render threads for web windows (served from the same 'site') to be grouped together to save memory. Group render process together thoughtfully by setting the same string value or disable rendering process grouping by setting false. */
	affinity?: string | boolean;
	alwaysOnTop?: boolean;
	autoShow?: boolean;
	alwaysOnTopLevel?: string | null;
	showTaskbarIcon?: boolean;
	left?: number;
	top?: number;
	right?: number;
	bottom?: number;
	width?: number;
	height?: number;
	minWidth?: number;
	minHeight?: number;
	maxWidth?: number;
	maxHeight?: number;
	opacity?: number;
	x?: number;
	y?: number;
	bounds?: {
		left: number;
		top: number;
		right: number;
		bottom: number;
		width: number;
		height: number;
	};
	securityPolicy?: string;
	backgroundColor?: string;
	transparent?: boolean;
	permissions?: {
		System?: {};
		Window?: {};
		Application?: {};
	};
	// Array of window names saying who can call execJS on the window. @deprecated
	execJSWhitelist?: Array<string>;
	preload?: Array<any>;
	preloadScripts?: Array<any>;
	windowType?: string;
	windowTitleBarUrl?: string;
	childWindowOptions?: {
		window?: any;
		component?: any;
		foreign?: any;
	};
	launchGroupComponentData?: LaunchGroupComponentData;
	hideTitleBar?: boolean;
	external?: boolean;
	icons?: AppIcons;
	windowInterop?: WindowInterop;
	allowAutoArrange?: boolean;
};

/** All the possible window types, including their aliases used in config. */
export type WindowTypes =
	| "WebWindow"
	| "NativeWindow"
	| "assimilation"
	| "assimilated"
	| "native"
	| "FinsembleNativeWindow"
	| "application"
	| "WebApplication"
	| "composite" // DEPRECATED - current composite is a launchGroup
	| "launchGroup"
	| "Citrix"
	| "external";

export type MonitorChoice = number | "mine" | "primary" | "next" | "previous" | "all";

/**
 * All the possible position types that can be used to define the spawn viewport
 *
 * **"available"** Positioned according to the coordinates available on the monitor itself, less space claimed by the operating system (such as the windows toolbar).
 * For instance, `bottom:0` will place the new component with its bottom flush against the windows toolbar.
 *
 * **"monitor"** Positioned according to the absolute size of the monitor.
 * For instance, `top:0` will place the component overlapping the toolbar.
 *
 * **"relative"** Positioned relative to the relativeWindow.
 * For instance, `left:0;top:0` will join the top left corner of the new component with the top left corner of the relative window.
 *
 * **"virtual"** Positioned against coordinates on the virtual screen.
 *  The virtual screen is the full viewing area of all monitors combined into a single theoretical monitor.
 *
 * **"absolute"** Positioned using native operating system coordinates. Only available with showWindow, not with spawn.
 *
 */
export type PositionTypes = "available" | "monitor" | "relative" | "virtual" | "absolute";

export type SpawnOptions = {
	autoShow?: boolean;
	backgroundThrottling?: boolean;
	contextMenu?: boolean;
	"Content-Security-Policy"?: string | string[];
	permissions?: any;
	securityPolicy?: "trusted" | "untrusted";
	showTaskbarIcon?: boolean;
	cornerRounding?: {
		width?: number;
		height?: number;
	};
	opacity?: number;
	openLinksInExternalBrowser?: boolean;
	resizable?: boolean;
	alwaysOnTop?: boolean;
	isEvergreen?: boolean;
	windowSpawnTimeout?: number;
	windowTitleBarUrl?: string;
	customData?: Record<string, any>;

	/**
	 * A pixel value representing the distance from the top edge of the viewport as defined by "position".
	 * You can also use the percentage value, which represents the percentage distance from the top edge of the viewport relative to the viewport's height.
	 *
	 * <b>"adjacent"</b> will snap to the right edge of the spawning or relative window.
	 *
	 * <b>"center"</b> will center the window
	 *
	 * If neither left nor right are provided, then the default will be to stagger the window based on the last spawned window.
	 *
	 */
	left?: number | string;
	/**
	 * A pixel value representing the distance from the right edge of the viewport as defined by "position".
	 * You can also use the percentage value, which represents the percentage distance from the right edge of the viewport relative to the viewport's width.
	 *
	 */
	right?: number | string;
	/**
	 * A pixel value representing the distance from the top edge of the viewport as defined by "position".
	 * You can also use the percentage value, which represents the percentage distance from the top edge of the viewport relative to the viewport's height.
	 *
	 */
	top?: number | string;
	/**
	 * A pixel value representing the distance from the bottom edge of the viewport as defined by "position".
	 * You can also use the percentage value, which represents the percentage distance from the bottom edge of the viewport relative to the viewport's height.
	 *
	 */
	bottom?: number | string;
	/**
	 *  A pixel or percentage value.
	 */
	width?: number | string;
	/**
	 *  A pixel or percentage value.
	 */
	height?: number | string;
};

/** The parameters passed to Launcher.Spawn.
 *
 * If properties are duplicated between the the app configuration those passed as
 * parameters to `spawn`, parameters passed to `spawn` generally take precedence.
 */
export type SpawnParams = {
	/**
	 * Adds the spawned app to a tab collection.
	 * If you supply a tabCollectionId string, Finsemble adds the app to the specified tab collection.
	 * If you supply a boolean, Finsemble attempts to tab with the calling window or the supplied relativeWindow argument.
	 */
	addToTabCollection?: string | boolean;

	/**
	 * Defaults to false. Whether to add the new component to the workspace.
	 * This value takes priority over the addToWorkspace setting in the component's app config.
	 */
	addToWorkspace?: boolean;
	/**
	 * Used when windowType is "native" or "assimilation". Specifies the alias of a bundled asset.
	 */
	alias?: string;
	/**
	 *  Process affinity setting that allows render threads for web windows (served from the same 'site') to be grouped together to save memory. Group render process together thoughtfully by setting the same string value or disable rendering process grouping by setting false.
	 */
	affinity?: string | boolean;

	/**
	 * Used when windowType is "native" or "assimilation". Specifies the arguments to be sent to the application. This is used in conjunction with path.
	 * Arguments should be separated by spaces: `--arg1 foo --arg2 bar` except when `params.argumentsAsQueryString` is true, in which case set this parameter to be single string in URI format: `arg=1&arg=2`"
	 */
	arguments?: any;
	/**
	 * For native applications launched by URI: 1) the string is passed as the "arguments" parameter if appended as a query string; and 2) the automatically generated arguments described in "path" are appended to the query string
	 */
	argumentsAsQueryString?: boolean;
	/**
	 * Whether the component can group with other windows.
	 */
	canGroup?: boolean;
	/**
	 *  For use with permanent toolbars.
	 * The available space for other components will be reduced by the amount of space covered by the newly spawned component.
	 * Users will be prevented from moving windows to a position that covers the claimed space.
	 */
	claimMonitorSpace?: boolean;
	/**
	 * Type of component to spawn.
	 */
	component?: any;

	/**
	 * Populate the initial component state on spawn.
	 */
	componentState?: Record<string, any>;

	/**
	 * @deprecated Please use groupOnSpawn instead.
	 * If true, will automatically group the window with the "relative" window (grouping to the parent window unless specified in params.relativeWindow).
	 * Note that you must also position the window in a valid position for grouping, for example, by setting the "left" or "top" parameters to "adjacent".
	 */
	dockOnSpawn?: boolean;
	/**
	 * An array of parts of the monitor that the component can dock to. Valid values are `top` and `bottom`.
	 */
	dockable?: ["top", "bottom"] | ["bottom", "top"] | ["top"] | ["bottom"];
	/**
	 * Which part of the monitor that the component will dock to on spawn. Valid options are `top` and `bottom`. Only valid if combined with the `dockable` property.
	 */
	docked?: "top" | "bottom";
	/**
	 * Currently, components can only dock to the full width of the monitor. This parameter determines what height the component will be when docked to a monitor.
	 */
	dockedHeight?: number;
	/**
	 * Indicates that this window is ephemeral.
	 * An ephemeral window is a dialog, menu, or other window that is temporarily displayed but usually hidden.
	 * Ephemeral windows automatically have the following settings assigned: resizable: false, showTaskbarIcon: false, alwaysOnTop: true.
	 * **Note**: Use `options:{autoShow: false}` to prevent an ephemeral widow from showing automatically.
	 *
	 */
	ephemeral?: boolean;

	/**
	 * If true, will attempt to make the window have no edges outside the monitor boundary.
	 */
	forceOntoMonitor?: boolean | string;
	/**
	 * Optional group name. Adds windows to a group (unrelated to docking or linking) that is used for window management functions. If the group does not exist it will be created.
	 */
	groupName?: string;
	/**
	 * Groups the window to the `relativeWindow` on spawn. Note that you must also position the window in a valid position for grouping, for example, by setting the "left" or "top" parameters to "adjacent".
	 */
	groupOnSpawn?: boolean;
	/**
	 * Which monitor to place the new window.
	 *
	 * <b>"mine"</b> - Place the window on the same monitor as the calling window.
	 *
	 * Integer value from 0-n (0 being the primary monitor).
	 *
	 * <b>"primary"</b> indicates the user's primary monitor.
	 *
	 * <b>"all"</b> - Put a copy of the component on all monitors.
	 *
	 */
	monitor?: MonitorChoice;
	/**
	 * Name to give the component. If not provided, a random one will be generated.
	 */
	name?: string;
	/**
	 * @deprecated Please use windowType instead. Optional native application to launch with Assimilation service. Overrides what is passed in "component".
	 */
	native?: boolean;
	/**
	 * Properties to merge with the default windowDescriptor.
	 * Any value set here will be sent directly to the <code>WebWindow</code>, and will override the effect of relevant parameters to spawn(). By default, all Finsemble windows are frameless.
	 * FEA Permissions and window properties (such as always on top) are provided here.
	 */
	options?: SpawnOptions & {
		// These options are just used as one-off transports by different parts of the code.
		// TBD - How should these options be handled moving forward? Are they formally supported?
		newStack?: boolean;
		transparent?: any;
		cachedBounds?: any;
		windowState?: any;
		preloadScripts?: any;

		/**
		 * Name to give the component. Window name from the workspace service will be passed here.
		 */
		name?: string;

		minWidth?: number;

		/* @deprecated */
		execJSWhitelist?: any;
	};
	/**
	 * Used when windowType is "native" or "assimilation". Specifies the path to the application. The path can be:
	 * The name of an exe that is on the system path (e.g., <i>notepad.exe</i>).
	 * The full path to an executable on the user's machine (e.g., <i>C:\Program Files\app.exe</i>).
	 * A system installed URI (e.g., <i>myuri://advancedapplauncher</i>).
	 *
	 * When windowType is "native" then additional arguments will be automatically appended to the path or the URI. These arguments can be captured by the native application
	 * in order to tie it to Finsemble's window tracking. When building an application with finsemble.dll, this is handled automatically. Those arguments are:
	 *
	 * **uuid** - A generated UUID that uniquely identifies this window.
	 *
	 * **left** - The x coordinate of the new window
	 *
	 * **top** - The y coordinate of the new window
	 *
	 * **width** - The width of the new window
	 *
	 * **height** - The height of the new window
	 *
	 * **finsembleWindowName** - The name of the window in the Finsemble config
	 *
	 * **componentType** - The component type in the Finsemble config
	 *
	 * A common troublesome problem is when a native application needs to be launched from an intermediary application (such as a launcher or batch script). That intermediary
	 * application can pass these parameters which will allow the final application to connect back to Finsemble.
	 */
	path?: string;

	// definition is repeated for documentation build
	/**
	 * Defines a "viewport" for the spawn, with one of the Position Types:
	 *
	 *
	 * <b>available</b> - Positioned according to the coordinates available on the monitor itself, less space claimed by the operating system (such as the windows toolbar).
	 * For instance, `bottom:0` will place the new component with its bottom flush against the windows toolbar.
	 *
	 * **monitor** - Positioned according to the absolute size of the monitor.
	 * For instance, `top:0` will place the component overlapping the toolbar.
	 *
	 * **relative** - Positioned relative to the relativeWindow.
	 * For instance, `left:0;top:0` will join the top left corner of the new component with the top left corner of the relative window.
	 *
	 * **virtual** - Deprecated. Positioned against coordinates on the virtual screen.
	 *  The virtual screen is the full viewing area of all monitors combined into a single theoretical monitor.
	 *
	 * **absolute** - Positioned using native operating system coordinates. Only available with showWindow, not with spawn.
	 */
	position?: PositionTypes;
	/**
	 * Sets environment variables for a spawned native application. Create a map (JSON) object of names to values. This is only available when running Assimilation.
	 */
	env?: any;
	/**
	 * The window to use when calculating any relative launches.
	 * If not set then the window from which spawn() was called.
	 */
	relativeWindow?: WindowIdentifier;
	/**
	 * Optional url to launch. Overrides what is passed in "component".
	 */
	url?: string;
	/**
	 * Optional. Describes which type of component to spawn.
	 *
	 * <b>WebWindow</b> - A normal HTML window.
	 *
	 * <b>assimilation</b> - A window that is managed by the Finsemble assimilation process (usually a native window without source code access). Requires "path" to be specified, which may be the name of an executable on the system path, a system file path or system installed URI.
	 *
	 * <b>native</b> - A native window that has implemented finsemble.dll. Requires "path" to be specified.
	 *
	 * <b>application</b> - A standalone application that will be launched in its own browser process (giving it dedicated CPU and memory).
	 * This can also point to a standalone web application (such as from a third party).
	 *
	 * <b>composite</b> - DEPRECATED - Use launchGroup.
	 *
	 * <b>launchGroup</b> - A component composed of other components, usually group together. See `toSpawn` property.
	 */
	windowType?: WindowTypes;
	/**
	 * Component instances are often referred to in Finsemble API calls via a <a href="/docs/productivity/componentTypesAndWindowNames#windowidentifier">windowIdentifier</a>. A windowIdentifier is an object with both windowName and componentType properties.
	 * @private
	 */
	windowIdentifier?: WindowIdentifier;
	/**
	 * If true then the new window will act as a slave to the relativeWindow (or the launching window if relativeWindow is not specified).
	 * Slave windows will automatically close when their parent windows close.
	 */
	slave?: boolean;
	/** *
	 * @private
	 */
	spawnedByWorkspaceService?: boolean;
	/**
	 * Number of pixels to stagger (default when neither left, right, top or bottom are set).
	 */
	staggerPixels?: number;

	/**
	 * Where the spawn request is coming from.
	 * @private
	 */
	launchingWindow?: WindowIdentifier;
	/**
	 * Optional data to pass to the opening window.
	 * If set, then the spawned window can use <a href="/docs/reference/typedocs/modules/WindowClient#getSpawnData">WindowClient#getSpawnData</a> to retrieve the data.
	 */
	data?: any;
	/**
	 * A pixel value representing the distance from the left edge of the viewport as defined by "position". Must be numeric for absolute positions.
	 * You can also use the percentage value, which represents the percentage distance from the left edge of the viewport relative to the viewport's width.
	 *
	 * <b>"adjacent"</b> will snap to the right edge of the spawning or relative window.
	 *
	 * <b>"center"</b> will center the window
	 *
	 * If neither left nor right are provided, then the default will be to stagger the window based on the last spawned window.
	 *
	 */
	left?: number | string;
	/**
	 * A pixel value representing the distance from the right edge of the viewport as defined by "position".
	 * For non-absolute positions, you can also use the percentage value, which represents the percentage distance from the right edge of the viewport relative to the viewport's width.
	 *
	 */
	right?: number | string;
	/**
	 * A pixel value representing the distance from the top edge of the viewport as defined by "position".
	 * For non-absolute positions, you can also use the percentage value, which represents the percentage distance from the top edge of the viewport relative to the viewport's height.
	 *
	 */
	top?: number | string;
	/**
	 * A pixel value representing the distance from the bottom edge of the viewport as defined by "position".
	 * For non-absolute positions, you can also use the percentage value, which represents the percentage distance from the bottom edge of the viewport relative to the viewport's height.
	 *
	 */
	bottom?: number | string;
	/**
	 *  A pixel or percentage value.
	 */
	width?: number | string;
	/**
	 *  A pixel or percentage value.
	 */
	height?: number | string;
	/**
	 * Minimum width window can be resized to.
	 */
	minWidth?: number;
	/**
	 * Minimum height window can be resized to.
	 */
	minHeight?: number;
	/**
	 * Maximum height window can be resized to.
	 */
	maxHeight?: number;
	/**
	 * Maximum width window can be resized to.
	 */
	maxWidth?: number;
	/**
	 * If true, then visibility of window will not be changed. In other words, a hidden window will not be made visible.
	 */
	setBoundsOnly?: boolean;
	/**
	 * Optional - sets a tab title for the window.
	 */
	tabTitle?: string;
	/**
	 * An array of objects defining the components to spawn. Each entry may either represent
	 * a single component in the form `{componentType, spawnOptions}` or a tabbed window of components in the form
	 * `{components, spawnOptions}`, where `components` is an array of single component definitions (again in the form
	 * `{componentType, spawnOptions}`).
	 * Most arguments that are normally passed to `LauncherClient.spawn` are supported, although positioning will be
	 * interpreted as relative to the group's coordinates and dimensions for single components and ignored for tabbed
	 * components (as these use the position specified in the group's spawnOptions).
	 * **Note** - This param will only be taken into account if the `windowType` param is 'composite' OR 'launchGroup'.
	 */
	toSpawn?: Record<string, any>[];
	/**
	 * Defaults to false. Whether to close all components within a launchGroup component at the same time.
	 */
	closeComponentsTogether?: boolean;

	launchGroupComponentData?: LaunchGroupComponentData;

	/**
	 * @deprecated
	 */
	compositeComponentData?: CompositeComponentData; // @deprecated - @see type declaration

	windowTitleBarUrl?: string;

	frame?: boolean;

	// Interop parameters for window -- used by InteropService
	windowInterop?: WindowInterop;

	// optional spawn param to specify static connectId
	connectId?: string;

	/**
	 * If false, the window will not be focused when spawned.
	 */
	autoFocus?: boolean;
};

interface BootParams {
	stage?: "microkernel" | "kernel" | "pre-authentication" | "authentication" | "preuser" | "earlyuser" | "user";
	stopOnFailure?: boolean;
	autoStart?: boolean;
	customFailureMessage?: string;
	timeout?: number;
	dependencies?: string[];
	checkpoints?: any;
}

interface Foreign {
	agentLabel?: string;
	clients?: any;
	services?: {
		windowService?: {
			global?: boolean;
			allowSnapping?: boolean;
			isArrangable?: boolean;
			allowGrouping?: boolean;
			allowAutoArrange?: boolean;
			allowTabbing?: boolean;
			allowTiling?: boolean;
			allowMinimize?: boolean;
			allowMaximize?: boolean;
			shouldRegister?: boolean;
			manageWindowMovement?: boolean;
			[k: string]: any;
		};
		workspaceService?: {
			persistURL?: boolean;
			persistPath?: boolean;
			persistArguments?: boolean;
			[k: string]: any;
		};
		dockingService?: {
			canGroup?: boolean;
			additionalProperties?: false;
			[k: string]: any;
		};
		additionalProperties?: false;
		[k: string]: any;
	};
	components?: {
		"App Launcher"?: {
			launchableByUser?: boolean;
			[k: string]: any;
		};
		"Window Manager"?: {
			launchableByUser?: boolean;
			[k: string]: any;
			titleBarHeight?: number;
			disableBrowserView?: boolean;
			titlebarType?: string;
		};
		Toolbar?: {
			iconURL?: string;
			iconClass?: string;
			[k: string]: any;
		};
		[k: string]: any;
	};
	custom?: Record<string, any>;
	additionalProperties?: false;
	[k: string]: any;
}

export interface ComponentConfig {
	componentType?: string;
	bootParams?: BootParams;
	signatureKey?: {
		alg?: string;
		ext?: boolean;
		key_ops?: string[];
		kty?: string;
		n?: string;
		e?: string;
		[k: string]: any;
	};
	isAnApp?: boolean;
	appConfig?: Record<string, any>;
	interop?: {
		useLinker?: boolean;
		autoConnect?: boolean;
		selectConnect?: {
			authorize?: {
				name?: string;
				[k: string]: NonNullable<any>;
			}[];
			to?: {
				name?: string;
				[k: string]: NonNullable<any>;
			}[];
			from?: {
				name?: string;
				[k: string]: NonNullable<any>;
			}[];
			cc?: string;
			contextType?: string;
			fwd?: string;
			[k: string]: NonNullable<any>;
		}[];
		[k: string]: NonNullable<any>;
	};
	window?: Record<string, any>;
	components?: Record<string, ComponentConfig>;
	component?: {
		type?: string;
		category?: string;
		mode?: string;
		advertiseReceivers?: string[] | string;
		spawnOnStartup?: boolean;
		spawnOnAllMonitors?: boolean;
		canMinimize?: boolean;
		canMaximize?: boolean;
		useAppCatalog?: boolean;
		singleton?: boolean;
		showDevConsoleOnVisible?: boolean;
		allowQuickComponent?: string;
		neededClients?: string[];
		displayName?: string;
		centralLoggerNamePrefix?: string;
		preload?: boolean | string | string[];
		inject?: boolean | string;
		spawnOnHotkey?: string[];
		custom?: Record<string, any>;
		isUnknownComponent?: boolean;
	};
	foreign?: Foreign;
	childWindowOptions?: ComponentConfig;
	custom?: Record<string, any>;
	value?: any;
	windowTitleBarUrl?: string;
	params?: Record<string, any>;
	spawnOptions?: Record<string, any>;
	launchGroupComponentData?: LaunchGroupComponentData;
}

export type LaunchGroupTabbedComponent = { componentType: string; spawnOptions: Record<string, any> };

export type LaunchGroupTabbedComponents = {
	components: LaunchGroupTabbedComponent[];
	launchGroupComponentData: LaunchGroupComponentData;
	spawnOptions: SpawnOptions;
};

export type ComponentMutateParams = {
	/** Field to save. */
	field?: string;
	/** Fields to save. */
	fields?: { field: string }[];
	/** Key to store the data under. */
	key?: string;
	/** Whether the data is componentState or windowData. Defaults to "windowData" */
	stateVar?: "componentState" | "windowData";
	/** Topic that the data is stored under. */
	topic?: string;
	/** Value to save. */
	value?: any;
};

/**
 * A structure representing a collection of tabs. Tabs are associated with (but do not fully represent) a window. The tab
 * is primarily responsible for the title of the associated window. A tab collection tracks any number of tabs, the tab order
 * (position), title and the windowName (which is the actual ID of the window). As well, the currently promoted (active) tab
 * is specified here.
 *
 * This object is passed via remote-redux by way of TitleBarData. Do not overload it or updated it too frequently.
 */
export type TabCollection = {
	// Name of the tab collection (same as the key)
	tabCollectionId: string;
	/**
	 * The tabs. Ordering is implicit in the array.
	 */
	tabs: string[];
	/**
	 * The currently active tab within the collection.
	 */
	promotedWindowName: string;
	/**
	 * The number of pixels currently scrolled within the titlebar.
	 */
	scroll: number;
};

export type BumpConfig = {
	bumpBy: string;
	fixed: string;
	absolute: string;
	// This determines whether the mutation observer should be enabled to look for fixed elements being added to the page.
	monitorFixedAdditions: boolean;
	// This determines if heights of absolute and fixed elements should be adjusted. If FSBLHeader.adjustContentHeights is false then no.
	adjustContentHeights: boolean;
	// This determines whether bumping logic is in effect. If FSBLHeader.bumpElements is false then no.
	bumpFixedElements: boolean;
	// This determines if the scrollbar's css is modified so that it doesn't overlap with the title bar.
	hackScrollbar: boolean;
};

/**
 * Contains all the information necessary to render the WindowTitleBar. This is passed over remote-redux, so this object
 * should not contain anything extraneous to what the WindowTitleBar's UI needs to know about.
 */
export type TitleBarData = {
	maximized: boolean;
	alwaysOnTop: boolean;
	title: string | null;
	editable: boolean;
	groupable: boolean;
	isGrouped: boolean;
	tabCollectionId: string | null;
	enableTabbing: boolean;
	showMaximizeButton: boolean;
	// This can be set to false either because minimization is disabled, or because the window is not the topRightWindow in a group
	showMinimizeButton: boolean;
	showAlwaysOnTopButton: boolean;
	showCloneButton: boolean;
	showCloseButton: boolean;
	showShareButton: boolean;
	bumpConfig: BumpConfig | null;
	icon: IconDescriptor | null;
	toBeEjected: boolean;
};

export type WindowGroup = {
	windowNames: string[];
	// @deprecated The name of the window in the top right corner.
	topRightWindow: string;
	// The names of all windows in the top right corner
	topRightWindows: string[];
	// True if formed into a group, false if just snapped into a _potential_ group
	isGrouped: boolean;
	// Specifies whether the AOT flag is set for each individual window in the group
	ungroupedAlwaysOnTopState?: Record<string, Boolean>;
};

export type SafetyTimeout = ReturnType<typeof setTimeout> | null;

// WindowDescriptor is a bit bloated. In the window store we slim it up
export type WindowDescriptorScrubbed = Omit<WindowDescriptor, "childWindowOptions" | "manifest" | "permissions">;

export enum TabCloseStatus {
	closingStarted,
	closingCompleted,
	closingAborted,
}

// Data model for window service
export type WindowServiceStore = {
	// A lightweight slice that is accessed via remote-redux by the WindowTitleBar UI component and sub-components
	// 1) This should only contain entries for windows that have title bars
	// 2) This should only contain information that is used by the WindowTitleBar UI
	windowTitleBar: {
		// Each WindowTitleBar UI component renders itself based on the TitleBarData that is associated with the window it is running within
		windows: Record<string, TitleBarData>;
		// The dragging section appears when a drag operation starts and is removed when a drag operation ends
		dragging: null | {
			// The window name of the tab that is being dragged
			windowNameBeingDragged: string;
			// Which live window the user has dragged the tab into. null when not inside the WindowTitleBar area of any window
			inWindowName: string | null;
			// The tab position that it would occupy if dropped this moment. Null if it wouldn't be dropped in a valid position.
			temporaryPosition: number | null;
			// The width of the tab being moved
			tabWidth: number | null;
		};
		// collection id -> collection info
		tabCollections: Record<string, TabCollection>;
		useDOMBasedMovement: boolean;
		// the store to save status of all windows in tab collection which should be closed
		// collection id -> array of window names -> window status
		closingTabs: null | Record<string, Record<string, TabCloseStatus>>;
	};
	ux: {
		// Contains the name of the scrim window, established lazily
		scrimWindowName: string | null;
		resolverWindowName: string | null;
		highlightedWindowName: string | null;
		scrimSafetyTimeout: SafetyTimeout;
	};
	// Definitive store for the window service
	main: {
		// window name -> window state
		windows: Record<
			string,
			{
				titles: {
					titleFromDocument?: string | null;
					titleFromAPI?: string | null;
					titleFromConfig?: string | null;
					titleFromUser?: string | null;
					titleFromAppD?: string | null;
				};
				physicalWindowState: PhysicalWindowState;
			}
		>;
		/**
		 * We use a hybrid approach to maximization. Windows are always maximized natively (e.g. using BrowserWindow.maximize()). This includes single
		 * windows and also the promoted (visible) tab in a collection. The hidden windows however are given “emulated maximization”. While hidden, hey
		 * are resized to the same size as the, truly maximized, promoted window (this occurs because a resized event always accompanies a maximize and thus the
		 * hidden windows follow that resizing).
		 *
		 * Windows' “normalBounds” (the position and dimensions before maximizing) are saved in this section of the store. The `emulated`
		 * flag indicates whether the window was truly maximized or merely emulated.
		 *
		 * A user unmaximize action then depends on whether the window being unmaximized is truly OS maximized or just emulated.
		 * Either way, a reducer ensures that all of the hidden tabs are either naturally unmaximized (by calling "restore") or
		 * merely resized away from their emulated maximized state.
		 *
		 * Most of this logic is in the main module when processing `resize`, `resized`, and `maximize` events. The actions.maximize reducer in
		 * the windowTitleBar module and addTabToCollection also work to keep the emulation accurate.
		 *
		 * This is all done pretty much to eliminate the ugly flashing effect that comes from maximizing a hidden tab during promotion but
		 * also helps with restoring maximized collections from workspaces and dragging windows into maximized collections. It also works
		 * around a nuance where Electron considers a window to be maximized even if it is merely resized to the full size of the monitor, and
		 * a fallout idiosyncrasy that normalBounds can be set to the full monitor size when a maximized window changes visibility.
		 */
		maximization: Record<
			string,
			{
				normalBounds: Rectangle;
				maximizedBounds: Rectangle;
				emulated: boolean;
			}
		>;
		// Data that is available before a window is actually opened
		launchData: Record<
			string,
			{
				windowDescriptor: WindowDescriptorScrubbed;
				// true for windows that are part of the workspace (excludes UI components, services, window.open)
				workspaceWindow: boolean;
				userDefinedTitle?: string;
				// True if the app was launched through a window.open() call
				windowDotOpen: boolean;
			}
		>;
		// group id -> window group info
		groups: Record<string, WindowGroup>;
		// Contains the list of windows that have been requested to be closed by the workspace. This list is used
		// to circumvent window effects during workspace switches and closes. This speeds up and cleans up workspace closings/switches
		workspaceClosings: string[];
		// Kept up to date with the current docking operation. This is used to determine what to do when a tiling operation ends
		// Specifically, to prevent tabs from being removed from tab collections during a "swapping" operation
		dockingOperation: {
			type: "swapping" | "detaching" | "tiling" | null;
		};
	};
	finsembleConfig: FinsembleConfig;
	api: {};
	// Testing section. This is only used by unit tests
	root: {
		isVisible: boolean;
		// A list of actions not to display in dev tools
		quashedActions: string[];
		testing: {
			unitTestingEnabled: boolean;
			// Set with any runtime errors to display
			error: string;
		};
	};
	"@@cmds"?: any[];
};

export type WindowServiceRootStore = WindowServiceStore["root"];
export type WindowTitleBarStore = WindowServiceStore["windowTitleBar"];
export type WindowServiceMainStore = WindowServiceStore["main"];
export type WindowServiceUXStore = WindowServiceStore["ux"];
export type DraggingStatus = NonNullable<WindowTitleBarStore["dragging"]>;
export type DockingOperation = WindowServiceStore["main"]["dockingOperation"];

export type ActionWindowSetTest = {
	test: string;
};

export type ReduxPayloadEmpty = {};

export type ReduxPayloadWindowName = {
	targetWindowName: string;
};

export type ReduxPayloadWindowNames = {
	windowNames: string[];
	visibility: boolean;
};

export type ReduxPayloadTabPosition = {
	targetWindowName: string;
	position: number;
};
/**
 * Remove a window from a tab collection and place on desktop
 * Note: what about tiling?
 */
export type ReduxPayloadSetLocation = {
	targetWindowName: string;
	monitor: number;
	x: number;
	y: number;
};

export type ReduxPayloadAddLaunchData = {
	windowDescriptor: WindowDescriptor;
	tabTitleFromSpawn?: string;
	workspaceWindow: boolean;
	finsembleConfig: FinsembleConfig;
	windowDotOpen: boolean;
};

export type ReduxPayloadWindowTitle = {
	title: string | null;
};

/**
 * Generic action payload that can be used to set any binary state
 */
export type ReduxPayloadToggleState = {
	toggleState: boolean;
};

/**
 * Generic action payload that can be used to set any binary state but
 * targeting a specific window
 */
export type ReduxPayloadToggleStateWithTarget = {
	toggleState: boolean;
	targetWindowName: string;
};

export type ReduxPayloadScrollTabCollection = {
	scroll: number;
};

/**
 * Establish an entire collection, such as when restoring from workspace or spawning a collection
 */
export type ReduxPayloadCollection = {
	windowNames: string[];
	tabCollectionId?: string;
	promotedWindowName?: string;
};

export type ReduxPayloadUpdateClosingStatus = {
	tabCollectionId: string;
	windowName: string;
	status: TabCloseStatus;
};

export type ReduxPayloadAddToCollection = {
	windowNames: string[];
	tabCollectionId: string;
	position?: number;
	promotedWindowName?: string;
};

export type ReduxPayloadWorkspaceLoad = {
	tabCollections: TabCollection[];
};

export type ReduxPayloadEmulatedMaximization = {
	targetWindow: string;
	normalBounds: Rectangle;
	maximizedBounds: Rectangle;
};

/**
 * The docking system has a single choke point, publishGroupUpdate, where groups are reassessed and published.
 * This payload includes all known groups mapped to their array of window names and the top right window, which
 * can be used to determine whether to display a minimize button on that window.
 */
export type ReduxPayloadGroups = Record<string, WindowGroup>;

export type ReduxPayloadWindowFilter = {
	monitor?: string;
	windowList?: string[];
	windowName?: string;
};

export type TabbedGlobalSpawnParams = {
	left?: number | string;
	right?: number | string;
	top?: number | string;
	bottom?: number | string;
	width?: number | string;
	height?: number | string;
	position?: PositionTypes;
	launchingWindow?: WindowIdentifier;
	promotedTabPosition?: number;
	addToWorkspace?: boolean;
};
